// src/components/ResumeComparisonView.jsx

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Button } from "./ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";
import { Copy, CheckCircle2, Save } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import api from "../lib/api"; // Changed: Import api client instead of supabase

export const ResumeComparisonView = ({ sections, fullResumeText }) => {
  const [copiedSection, setCopiedSection] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleCopySection = async (sectionTitle, content) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedSection(sectionTitle);
      toast.success(`${sectionTitle} copied!`);
      setTimeout(() => setCopiedSection(null), 2000);
    } catch (error) {
      toast.error("Failed to copy");
    }
  };

  // Changed: Use Express API instead of direct Supabase calls
  const handleSaveResume = async () => {
    setSaving(true);
    try {
      // Get the latest resume to check if one exists
      const existingResume = await api.getLatestResume();

      if (existingResume) {
        // UPDATE existing resume
        await api.updateResume(existingResume.id, {
          file_name: `optimized_resume_${Date.now()}.txt`,
          parsed_data: { fullText: fullResumeText, optimized: true },
        });
        toast.success("Resume updated successfully!");
      } else {
        // INSERT new resume (edge case: user somehow deleted their initial upload)
        await api.createResume({
          file_name: `optimized_resume_${Date.now()}.txt`,
          parsed_data: { fullText: fullResumeText, optimized: true },
        });
        toast.success("Optimized resume saved successfully!");
      }
    } catch (error) {
      console.error("Error saving resume:", error);
      toast.error("Failed to save resume");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="w-full">
      <CardHeader className="p-3 sm:p-6">
        <div className="flex flex-col sm:flex-row items-start justify-between gap-3">
          <div className="flex-1">
            <CardTitle className="text-xl sm:text-2xl">
              Your Optimized Resume
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm">
              Review the complete resume below, copy it, or explore
              section-by-section details.
            </CardDescription>
          </div>
          <Button
            onClick={handleSaveResume}
            disabled={saving}
            className="w-full sm:w-auto shrink-0"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? "Saving..." : "Save to Database"}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6 p-3 sm:p-6 pt-0">
        {/* Full Resume Preview Section */}
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <h3 className="text-base sm:text-lg font-semibold">
              Complete Resume
            </h3>
            <Button
              variant="default"
              onClick={() => handleCopySection("Full Resume", fullResumeText)}
              className="w-full sm:w-auto"
            >
              {copiedSection === "Full Resume" ? (
                <>
                  <CheckCircle2 className="h-4 w-4 mr-2 text-green-500" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4 mr-2" />
                  Copy Full Resume
                </>
              )}
            </Button>
          </div>
          <div className="bg-muted/50 p-3 sm:p-6 rounded-lg border overflow-x-auto">
            <pre className="whitespace-pre-wrap text-xs sm:text-sm font-sans leading-relaxed">
              {fullResumeText}
            </pre>
          </div>
        </div>

        {/* Section-by-Section Breakdown */}
        <div>
          <h3 className="text-base sm:text-lg font-semibold mb-3">
            Section-by-Section Breakdown
          </h3>
          <Accordion type="multiple" className="w-full">
            {sections.map((section, index) => (
              <AccordionItem key={index} value={`section-${index}`}>
                <AccordionTrigger className="text-base sm:text-lg font-semibold px-3 sm:px-0">
                  {section.title}
                </AccordionTrigger>
                <AccordionContent className="space-y-4 px-3 sm:px-0">
                  <div className="bg-muted/50 p-3 sm:p-4 rounded-lg border overflow-x-auto">
                    <div className="flex flex-col sm:flex-row items-start justify-between gap-2 mb-2">
                      <h4 className="font-medium text-xs sm:text-sm text-muted-foreground">
                        Optimized Content
                      </h4>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          handleCopySection(section.title, section.content)
                        }
                        className="w-full sm:w-auto"
                      >
                        {copiedSection === section.title ? (
                          <>
                            <CheckCircle2 className="h-4 w-4 mr-2 text-green-500" />
                            Copied!
                          </>
                        ) : (
                          <>
                            <Copy className="h-4 w-4 mr-2" />
                            Copy
                          </>
                        )}
                      </Button>
                    </div>
                    <pre className="whitespace-pre-wrap text-xs sm:text-sm font-sans">
                      {section.content}
                    </pre>
                  </div>

                  <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 sm:p-4">
                    <h4 className="font-semibold text-xs sm:text-sm mb-2 text-blue-900 dark:text-blue-100">
                      What Changed
                    </h4>
                    <p className="text-xs sm:text-sm text-blue-800 dark:text-blue-200 leading-relaxed">
                      {section.whatChanged}
                    </p>
                  </div>

                  <div className="bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-lg p-3 sm:p-4">
                    <h4 className="font-semibold text-xs sm:text-sm mb-2 text-green-900 dark:text-green-100">
                      Why This Works
                    </h4>
                    <p className="text-xs sm:text-sm text-green-800 dark:text-green-200 leading-relaxed">
                      {section.why}
                    </p>
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </CardContent>
    </Card>
  );
};
